import { useState, useEffect, useMemo, useRef } from 'react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, Shield, ShieldCheck, ArrowUpDown, ArrowUp, ArrowDown, UserCircle, Trash2, LogIn } from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  fetchUsers,
  fetchGroups,
  fetchPermissionsMap,
  userGroupsFromMap,
  deleteUser,
  loginAsUser,
  type User,
  type Group,
} from '@/lib/traccar-api';
import { fetchResellerAdminUsers } from '@/lib/reseller-api';
import { useResellerAccess } from '@/hooks/useResellerAccess';
import { CreateUserDialog } from '@/components/users/CreateUserDialog';
import { EditUserDialog } from '@/components/users/EditUserDialog';
import { EditUserDevicesDialog } from '@/components/users/EditUserDevicesDialog';
import { EditUserGroupsDialog } from '@/components/users/EditUserGroupsDialog';
import { EditUserManagedUsersDialog } from '@/components/users/EditUserManagedUsersDialog';

type SortField = 'id' | 'name' | 'email' | 'administrator';
type SortDirection = 'asc' | 'desc';

const Users = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useTraccarSession();
  const { isReseller } = useResellerAccess();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const lastSelectedIndex = useRef<number | null>(null);

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
    enabled: isAuthenticated
  });

  const { data: allGroups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: fetchGroups,
    enabled: isAuthenticated,
  });

  const { data: permsMap } = useQuery({
    queryKey: ['permissions-map'],
    queryFn: fetchPermissionsMap,
    enabled: isAuthenticated,
  });

  const { data: resellerAdminUsers = [] } = useQuery({
    queryKey: ['reseller-admin-users'],
    queryFn: fetchResellerAdminUsers,
    enabled: isAuthenticated && isReseller,
  });

  const resellerUserIds = useMemo(
    () => new Set(resellerAdminUsers.map(u => u.traccar_user_id)),
    [resellerAdminUsers]
  );

  const userGroupsMap: Record<number, Group[]> = useMemo(() => {
    if (!permsMap) return {};
    const out: Record<number, Group[]> = {};
    for (const u of users) out[u.id] = userGroupsFromMap(u.id, allGroups, permsMap);
    return out;
  }, [users, allGroups, permsMap]);

  const isLoading = usersLoading && users.length === 0;


  // Clear selection when search changes
  useEffect(() => {
    setSelectedUsers(new Set());
    lastSelectedIndex.current = null;
  }, [searchQuery]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-primary" />
      : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
  };

  const sortedUsers = useMemo(() => {
    const filtered = users.filter(user => {
      const query = searchQuery.toLowerCase();
      return (
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
    });

    return [...filtered].sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'id':
          return (a.id - b.id) * dir;
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'email':
          return a.email.localeCompare(b.email) * dir;
        case 'administrator': {
          const adminA = a.administrator ? 1 : 0;
          const adminB = b.administrator ? 1 : 0;
          return (adminB - adminA) * dir;
        }
        default:
          return 0;
      }
    });
  }, [users, searchQuery, sortField, sortDirection]);

  const handleSelectUser = (index: number, userId: number, checked: boolean, shiftKey: boolean) => {
    const newSelected = new Set(selectedUsers);

    if (shiftKey && lastSelectedIndex.current !== null && checked) {
      // Shift+click: select range
      const start = Math.min(lastSelectedIndex.current, index);
      const end = Math.max(lastSelectedIndex.current, index);
      for (let i = start; i <= end; i++) {
        newSelected.add(sortedUsers[i].id);
      }
    } else {
      if (checked) {
        newSelected.add(userId);
      } else {
        newSelected.delete(userId);
      }
    }

    lastSelectedIndex.current = index;
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(sortedUsers.map(u => u.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const isAllSelected = sortedUsers.length > 0 && selectedUsers.size === sortedUsers.length;
  const isSomeSelected = selectedUsers.size > 0 && selectedUsers.size < sortedUsers.length;

  const deleteMutation = useMutation({
    mutationFn: async (userIds: number[]) => {
      await Promise.all(userIds.map(id => deleteUser(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSelectedUsers(new Set());
      toast.success(t('users.deleteSuccess'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('users.deleteError'));
    },
  });

  const loginAsMutation = useMutation({
    mutationFn: async (user: User) => {
      const response = await loginAsUser(user.id);
      if (!response.ok) {
        throw new Error('Failed to login as user');
      }
      return user;
    },
    onSuccess: () => {
      window.location.href = '/';
    },
    onError: (error: Error) => {
      toast.error(error.message || t('users.loginAsError'));
    },
  });

  const handleBulkDelete = () => {
    const userIds = Array.from(selectedUsers);
    deleteMutation.mutate(userIds);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const adminCount = users.filter(u => u.administrator).length;
  const regularCount = users.filter(u => !u.administrator).length;

  const headerContent = (
    <div className="flex items-center gap-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('users.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 w-32 sm:w-52"
        />
      </div>
      <CreateUserDialog />
    </div>
  );

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <div className="hidden sm:flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <UserCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('users.total')}:</span>
              <span className="font-medium">{users.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">{t('users.administrators')}:</span>
              <span className="font-medium text-primary">{adminCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('users.regular')}:</span>
              <span className="font-medium">{regularCount}</span>
            </div>
          </div>

          {selectedUsers.size > 0 && (
            <div className="flex items-center gap-2 bg-muted/50 px-3 py-1.5 rounded-lg border">
              <span className="text-sm font-medium">
                {t('users.selected', { count: selectedUsers.size })}
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={deleteMutation.isPending}
                className="gap-1"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {t('common.delete')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedUsers(new Set())}
                disabled={deleteMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-card [&_.relative]:overflow-x-hidden">
          <Table className="table-fixed sm:table-auto [&_th]:px-2 [&_td]:px-2 sm:[&_th]:px-4 sm:[&_td]:px-4">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8 sm:w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label={t('users.selectAll')}
                    className={isSomeSelected ? 'data-[state=checked]:bg-primary' : ''}
                    {...(isSomeSelected ? { 'data-state': 'indeterminate' } : {})}
                  />
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    {t('users.name')}
                    <SortIcon field="name" />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50 hidden sm:table-cell"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-1">
                    {t('users.email')}
                    <SortIcon field="email" />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50 hidden sm:table-cell"
                  onClick={() => handleSort('administrator')}
                >
                  <div className="flex items-center gap-1">
                    {t('users.role')}
                    <SortIcon field="administrator" />
                  </div>
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  {t('users.groups')}
                </TableHead>
                <TableHead
                  className="w-20 text-right cursor-pointer hover:bg-muted/50 hidden sm:table-cell"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center justify-end gap-1">
                    {t('users.id')}
                    <SortIcon field="id" />
                  </div>
                </TableHead>
                <TableHead className="w-24 sm:w-auto text-right">
                  <span className="hidden sm:inline">{t('users.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {searchQuery ? t('users.noUsersMatch') : t('users.noUsers')}
                  </TableCell>
                </TableRow>
              ) : (
                sortedUsers.map((user, index) => (
                  <TableRow
                    key={user.id}
                    className={`hover:bg-muted/50 ${selectedUsers.has(user.id) ? 'bg-muted/30' : ''}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedUsers.has(user.id)}
                        onCheckedChange={(checked) => handleSelectUser(index, user.id, checked as boolean, false)}
                        onClick={(e) => {
                          if (e.shiftKey) {
                            e.preventDefault();
                            handleSelectUser(index, user.id, !selectedUsers.has(user.id), true);
                          }
                        }}
                        aria-label={t('users.selectUser', { name: user.name })}
                      />
                    </TableCell>
                    <TableCell className="font-medium overflow-hidden max-w-0 sm:max-w-[250px]">
                      <span className="block truncate">{user.name}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell truncate max-w-[200px]">{user.email}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {resellerUserIds.has(user.id) && (
                          <Badge variant="outline" className="gap-1 text-blue-600 border-blue-300">
                            {t('users.resellerUser')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(userGroupsMap[user.id] ?? []).slice(0, 3).map(g => (
                          <Badge key={g.id} variant="outline" className="text-xs max-w-[10rem] inline-block truncate align-middle">
                            {g.name}
                          </Badge>
                        ))}
                        {(userGroupsMap[user.id] ?? []).length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{(userGroupsMap[user.id] ?? []).length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground font-mono text-sm hidden sm:table-cell">
                      {user.id}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0 sm:gap-1">
                        <EditUserDialog user={user} />
                        <EditUserDevicesDialog user={user} />
                        <EditUserGroupsDialog user={user} />
                        <EditUserManagedUsersDialog user={user} />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loginAsMutation.mutate(user)}
                          disabled={loginAsMutation.isPending}
                          className="h-7 w-7 p-0"
                        >
                          {loginAsMutation.isPending && loginAsMutation.variables?.id === user.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <LogIn className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Users;
